import { useState, useEffect, useCallback } from 'react';

const LOCK_KEY_PREFIX = 'exam_lock_';
const HEARTBEAT_INTERVAL_MS = 2000;
const STALE_THRESHOLD_MS = 15000;

interface LockData {
  tabId: string;
  lastActivity: number;
}

function getTabId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `tab_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

/**
 * Hook que impide que el examen se abra en múltiples pestañas.
 * Usa localStorage + heartbeat para detectar otra pestaña activa.
 * @param lockId - Identificador único del intento (attemptId, token, assessmentId)
 * @returns { locked: boolean } - true si otra pestaña tiene el examen abierto
 */
export function useExamLock(lockId: string | null): { locked: boolean } {
  const [locked, setLocked] = useState(false);
  const tabIdRef = useState(() => getTabId())[0];

  const tryAcquireLock = useCallback((): boolean => {
    if (!lockId) return true;
    const key = `${LOCK_KEY_PREFIX}${lockId}`;
    try {
      const raw = localStorage.getItem(key);
      const now = Date.now();
      if (!raw) {
        const data: LockData = { tabId: tabIdRef, lastActivity: now };
        localStorage.setItem(key, JSON.stringify(data));
        return true;
      }
      const data = JSON.parse(raw) as LockData;
      if (data.tabId === tabIdRef) {
        localStorage.setItem(key, JSON.stringify({ ...data, lastActivity: now }));
        return true;
      }
      const stale = now - data.lastActivity > STALE_THRESHOLD_MS;
      if (stale) {
        localStorage.setItem(key, JSON.stringify({ tabId: tabIdRef, lastActivity: now }));
        return true;
      }
      return false;
    } catch {
      return true;
    }
  }, [lockId, tabIdRef]);

  const releaseLock = useCallback(() => {
    if (!lockId) return;
    const key = `${LOCK_KEY_PREFIX}${lockId}`;
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const data = JSON.parse(raw) as LockData;
        if (data.tabId === tabIdRef) {
          localStorage.removeItem(key);
        }
      }
    } catch {
      // Ignorar
    }
  }, [lockId, tabIdRef]);

  useEffect(() => {
    if (!lockId) {
      setLocked(false);
      return;
    }
    const acquired = tryAcquireLock();
    setLocked(!acquired);

    const heartbeat = setInterval(() => {
      const stillAcquired = tryAcquireLock();
      if (!stillAcquired) setLocked(true);
    }, HEARTBEAT_INTERVAL_MS);

    const handleBeforeUnload = () => releaseLock();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const acquired = tryAcquireLock();
        setLocked(!acquired);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(heartbeat);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      releaseLock();
    };
  }, [lockId, tryAcquireLock, releaseLock]);

  return { locked };
}
